import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output} from '@angular/core';
import {NgRedux, select} from '@angular-redux/store';
import {CurrentSelections, Data, FilterCriteria, IAppState, INITIAL_STATE} from '../store/IAppState';
import {
  ExportType,
  getAppConfigValue,
  getOppositeNodeExpansionState,
  getSelectedDateRange,
  isFeatureEnabled,
  lagPeriods,
  nodeExpansionIsActive
} from '../shared/helpers';
import {CptViewType, NodeExpansionState} from '../shared/enums';
import {Router} from '@angular/router';
import {overviewPageRoute} from '../shared/routes';
import {MatDialogRef} from '@angular/material/dialog';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {
  calculateDepth,
  canDisplayExportOption,
  collectPdfOptions,
  ExportMetricOption,
  exportToExcelOrCsv,
  exportToPdf,
  getRequiredId,
  MultilevelExcelExportMetrics,
  MultilevelExportFiltersData,
  MultilevelExportOption,
  selectOrDeselectParents
} from './export-dialog-helper';
import {EmDimension, FeatureToggleSetting, PdfExportData, UserProfile} from '../shared/models';
import {ExcelService, ExcelServiceToken} from '../services/excel.service';
import {AnalyticsService, AnalyticsServiceToken} from '../analytics/analytics.service';
import {CSV, CSVToken} from '../services/csv.service';
import {
  calculateBreadcrumbs,
  composeCurrentLocationFiltersForCsvExport,
  composeCurrentLocationFiltersForExcelExport,
  getCptGroupBreadcrumb,
  getPayerHierarchyString
} from '../shared/export/export-helper';
import {DEFAULT_MEMBER_BILLING_AREA} from '../store/DefaultValues';
import {FeatureToggleEntries} from '../shared/feature-toggle-settings-enum';
import {showClinicalFingerprint} from '../shared/test/ontology-helper';
import {invoiceStatusTypes, visitTypes} from '../shared/constants';
import * as _ from 'lodash';
import {AppConfigEntries} from '../shared/app-config-settings-enum';
import {CfpByMultilevel, ProcedureSummaryDrillImpl} from '../clinical-fingerprint/ClinicalSummary';
import {CptGroup} from '../clinical-fingerprint/procedure-summary/ProcedureSummaryModels';

@Component({
  selector: 'app-export-dialog-multilevel',
  templateUrl: './export-dialog-multilevel.component.html',
  styleUrls: ['./export-dialog-multilevel.component.scss']
})
export class ExportDialogMultilevelComponent implements OnInit, OnDestroy {

  constructor(private readonly ngRedux: NgRedux<IAppState>, public router: Router,
              @Inject(ExcelServiceToken) private readonly excelService: ExcelService,
              @Inject(CSVToken) private readonly csv: CSV,
              @Inject(AnalyticsServiceToken) private readonly analyticsService: AnalyticsService,
              public dialogRef: MatDialogRef<ExportDialogMultilevelComponent>) {
  }

  @Output() afterPdfExport = new EventEmitter<[PdfExportData, EmDimension[], boolean]>();
  @select(['display', 'isExporting'])
  private readonly isExporting$: Observable<boolean>;
  @select(['data'])
  private readonly data$: Observable<Data>;
  ExportType = ExportType;
  generalMultilevelExportOptions: MultilevelExportOption[] = [];
  displayedMultilevelExportOptions: MultilevelExportOption[] = [];
  pdfOptions: MultilevelExportOption[] = [];
  currentExportFiltersData: MultilevelExportFiltersData = {};
  maxProviders = 500;
  maxSpecialties = 500;
  pdfPermitted = false;
  chosenExportType: ExportType = ExportType.excel;
  defaultCheckAllowed = true;
  private defaultExportType = ExportType.excel;
  cptViewType: CptViewType;
  private canHideDenials = true;
  private canHideProcedureSummary = true;
  private canShowClinical = false;
  private canShowMultiLocation = false;
  private canHideNpvByLocation = true;
  private shouldShowWrvuDepartmentCfteAdjusted = true;
  greyMetricMessage = 'Metrics in gray are not yet loaded. Navigate to the metric page to load and export';
  private stateData: Data;
  private display: CurrentSelections;
  private filters: FilterCriteria;
  private subscription: Subscription;
  private anyMetricsLoading = true;
  exportPermitted = false;
  isExporting = false;
  npvTelehealthEnabled: boolean;
  private emTelehealthEnabled: boolean;
  NodeExpansionState = NodeExpansionState;
  private canShowCfteWrvu: boolean;
  private canShowCfteCount: boolean;


  ngOnInit(): void {
    this.subscription = combineLatest([this.isExporting$, this.data$]).subscribe(([exporting, data]:
      [boolean, Data]) => {
      this.isExporting = exporting;
      this.stateData = data;
      this.display = this.ngRedux.getState().display;
      this.filters = this.ngRedux.getState().filters;
      this.canShowClinical = showClinicalFingerprint(this.filters.nodePath);
      this.cptViewType = this.display.cptViewType;
      const {featureToggleSettings, userProfile} = this.stateData;
      this.setFeatureToggleDependencies(featureToggleSettings, userProfile);
      this.currentExportFiltersData.breadcrumb = calculateBreadcrumbs(this.filters.nodePath);
      this.currentExportFiltersData.dataRange = 'Selected Date Range: ' +
        getSelectedDateRange(this.filters.dateRange);
      this.composeCurrentLocationFiltersForExport(data, this.filters);
      this.currentExportFiltersData.payer = getPayerHierarchyString(this.filters);
      this.currentExportFiltersData.billing = 'Billing Area: ' +
        (this.filters.memberBillingArea.memberBillingAreaDescription.length > 0 ?
          this.filters.memberBillingArea.memberBillingAreaDescription
          : DEFAULT_MEMBER_BILLING_AREA.memberBillingAreaDescription);
      this.currentExportFiltersData.lag = 'Lag Period: ' +
        lagPeriods.find(l => l.key === this.filters.lagKey)?.name || '';
      this.currentExportFiltersData.invoice = 'Invoice Type: ' +
        invoiceStatusTypes[this.filters.invoiceStatus].statusText;
      this.currentExportFiltersData.visitType = 'Visit Type: ' + visitTypes[this.filters.telehealthFlag ?? 0].text;
      this.composeCptGroupBreadcrumb(this.display.procedureSummaryDrill, this.display.selectedCptGroup,
        this.stateData.clinicalFingerprintMultilevelData.cfpByDepartment);
      this.generalMultilevelExportOptions = [];
      const multilevelExcelExportMetrics = MultilevelExcelExportMetrics();
      calculateDepth(multilevelExcelExportMetrics, 0);
      this.getMultilevelExcelExportMetrics(multilevelExcelExportMetrics);
      this.setPdfPermitted();
      this.setDefault(this.router.url, this.displayedMultilevelExportOptions);
      this.canShowCfteWrvu = !isFeatureEnabled(FeatureToggleEntries.HIDE_PROCEDURE_SUMMARY_CFTE_WRVUS,
        this.stateData.featureToggleSettings, this.stateData.userProfile);
      this.canShowCfteCount = !isFeatureEnabled(FeatureToggleEntries.HIDE_PROCEDURE_SUMMARY_CFTE_COUNT,
        this.stateData.featureToggleSettings, this.stateData.userProfile);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private setFeatureToggleDependencies(featureToggleSettings: FeatureToggleSetting[], userProfile: UserProfile): void {
    this.canHideDenials = !isFeatureEnabled(FeatureToggleEntries.DENIALS_METRIC, featureToggleSettings, userProfile);
    this.canHideProcedureSummary = !isFeatureEnabled(FeatureToggleEntries.PROCEDURE_SUMMARY, featureToggleSettings, userProfile);
    this.canHideNpvByLocation = !isFeatureEnabled(FeatureToggleEntries.NPV_BY_LOCATION, featureToggleSettings, userProfile);
    this.npvTelehealthEnabled = isFeatureEnabled(FeatureToggleEntries.TELEHEALTH_BENCHMARKS_NPV, featureToggleSettings, userProfile);
    this.emTelehealthEnabled = isFeatureEnabled(FeatureToggleEntries.TELEHEALTH_BENCHMARKS_EM, featureToggleSettings, userProfile);
    this.canShowMultiLocation = isFeatureEnabled(FeatureToggleEntries.LOCATION_MULTI_SELECT, featureToggleSettings, userProfile);
    this.shouldShowWrvuDepartmentCfteAdjusted = !isFeatureEnabled(FeatureToggleEntries.HIDE_WRVU_BY_DEPARTMENT_CFTE_ADJ_WRVUS,
      featureToggleSettings, userProfile);
  }

  private composeCptGroupBreadcrumb(procedureSummaryDrill: ProcedureSummaryDrillImpl | undefined, cptGroup: CptGroup | undefined,
                                    cfpData: CfpByMultilevel[]) {
    this.currentExportFiltersData.cptGroupBreadcrumb = getCptGroupBreadcrumb(procedureSummaryDrill, cptGroup, cfpData);
  }

  private composeCurrentLocationFiltersForExport(data: Data, filters: FilterCriteria) {
    const maxLocations = +getAppConfigValue(AppConfigEntries.LOCATIONS_TO_SHOW_IN_EXPORT, data.applicationConfigurationSettings) || 5;
    const selectedLocations = data.memberLocations.filter(loc => loc.currentlySelected);
    const additionalLocations = selectedLocations.length - maxLocations;
    const isSingleLocation = !this.canShowMultiLocation || !selectedLocations.length;
    this.currentExportFiltersData.excelLocation = composeCurrentLocationFiltersForExcelExport(isSingleLocation,
      selectedLocations, maxLocations, additionalLocations, filters);
    this.currentExportFiltersData.csvLocation = composeCurrentLocationFiltersForCsvExport(isSingleLocation,
      selectedLocations, filters);
  }

  inspectHasLoaded(): void {
    const equalProductivitySummary = _.isEqual(this.stateData.productivitySummaryData,
      INITIAL_STATE.data.productivitySummaryData);
    const equalProductivitySnapshot = _.isEqual(this.stateData.providerProductivityMultilevelData,
      INITIAL_STATE.data.providerProductivityMultilevelData);
    const equalProductivityTrend = _.isEqual(this.stateData.mergedProductivityTrend,
      INITIAL_STATE.data.mergedProductivityTrend);
    const equalNpvSummary = _.isEqual(this.stateData.summaryNewPatientVisitData,
      INITIAL_STATE.data.summaryNewPatientVisitData);
    const equalNpvSnapshot = _.isEqual(this.stateData.newPatientVisitMultilevelData,
      INITIAL_STATE.data.newPatientVisitMultilevelData);
    const equalNpvTrend = _.isEqual(this.stateData.newPatientVisitTrendData,
      INITIAL_STATE.data.newPatientVisitTrendData);
    const equalEM_Snapshot = _.isEqual(this.stateData.evaluationManagementMultilevelData,
      INITIAL_STATE.data.evaluationManagementMultilevelData);
    const equalEM_summary = _.isEqual(this.stateData.summaryEvaluationManagementData,
      INITIAL_STATE.data.summaryEvaluationManagementData);
    const equalCfp = _.isEqual(this.stateData.clinicalSummaryConsolidationData,
      INITIAL_STATE.data.clinicalSummaryConsolidationData);
    const equalCfpMultilevel = _.isEqual(this.stateData.clinicalFingerprintMultilevelData,
      INITIAL_STATE.data.clinicalFingerprintMultilevelData);
    const equalCollectionSummary = _.isEqual(this.stateData.summaryCollectionsData,
      INITIAL_STATE.data.summaryCollectionsData);
    const equalCollectionSnapshot = _.isEqual(this.stateData.matchedCollectionsByMultiLevelByNodePathData,
      INITIAL_STATE.data.matchedCollectionsByMultiLevelByNodePathData);
    const equalPayerCollection = _.isEqual(this.stateData.payerCollectionsData,
      INITIAL_STATE.data.payerCollectionsData);
    const equalPayerByLevelCollection = _.isEqual(this.stateData.matchedCollectionsByPayerByMultiLevelByNodePathData,
      INITIAL_STATE.data.matchedCollectionsByPayerByMultiLevelByNodePathData);
    const equalDenials = _.isEqual(this.stateData.denialsByMultiLevelData,
      INITIAL_STATE.data.denialsByMultiLevelData);
    const equalDenialsPayer = _.isEqual(this.stateData.denials, INITIAL_STATE.data.denials);

    const wrvuSnapshotLoaded = !(equalProductivitySummary || equalProductivitySnapshot);
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.WRVU_BY_DEPARTMENT,
      ExportMetricOption.WRVU_BY_SPECIALTY, ExportMetricOption.WRVU_BY_PROVIDER,
      ExportMetricOption.WRVU_SPECIALTY_PERFORMANCE].includes(o.id)).forEach(c => {
      c.loaded = wrvuSnapshotLoaded;
    });
    const wrvuTrendLoaded = !(equalProductivitySummary || equalProductivityTrend);
    const wrvuTrendOption = this.generalMultilevelExportOptions.find(o => o.id === ExportMetricOption.WRVU_TREND);
    if (wrvuTrendOption) {
      wrvuTrendOption.loaded = wrvuTrendLoaded;
    }
    const npvSnapshotLoaded = !(equalNpvSnapshot || equalNpvSummary);
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.NPV_BY_DEPARTMENT,
      ExportMetricOption.NPV_BY_SPECIALTY, ExportMetricOption.NPV_BY_PROVIDER].includes(o.id)).forEach(c => {
      c.loaded = npvSnapshotLoaded;
    });
    const npvTrendLoaded = !(equalNpvTrend || equalNpvSummary);
    const npvTrendOption = this.generalMultilevelExportOptions.find(o => o.id === ExportMetricOption.NPV_TREND);
    if (npvTrendOption) {
      npvTrendOption.loaded = npvTrendLoaded;
    }
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.NPV_BY_LOCATION,
      ExportMetricOption.NPV_BY_LOCATION_BY_DEPARTMENT, ExportMetricOption.NPV_BY_LOCATION_BY_SPECIALTY,
      ExportMetricOption.NPV_BY_LOCATION_BY_PROVIDER].includes(o.id)).forEach(c => {
        c.loaded = true; // TODO: This should depend on the data.
    });
    const emLoaded = !(equalEM_Snapshot || equalEM_summary);
    this.generalMultilevelExportOptions.filter(o => [
      ExportMetricOption.EM,
      ExportMetricOption.EM_OUTPATIENT,
      ExportMetricOption.EM_OUTPATIENT_BY_DEPARTMENT,
      ExportMetricOption.EM_OUTPATIENT_BY_SPECIALTY,
      ExportMetricOption.EM_OUTPATIENT_BY_PROVIDER,
      ExportMetricOption.EM_OUTPATIENT_TREND,
      ExportMetricOption.EM_INPATIENT,
      ExportMetricOption.EM_INPATIENT_BY_DEPARTMENT,
      ExportMetricOption.EM_INPATIENT_BY_SPECIALTY,
      ExportMetricOption.EM_INPATIENT_BY_PROVIDER,
      ExportMetricOption.EM_INPATIENT_TREND,
      ExportMetricOption.EM_EMERGENCY,
      ExportMetricOption.EM_EMERGENCY_BY_DEPARTMENT,
      ExportMetricOption.EM_EMERGENCY_BY_SPECIALTY,
      ExportMetricOption.EM_EMERGENCY_BY_PROVIDER,
      ExportMetricOption.EM_EMERGENCY_TREND,
      ExportMetricOption.EM_EYE_EXAM,
      ExportMetricOption.EM_EYE_EXAM_BY_DEPARTMENT,
      ExportMetricOption.EM_EYE_EXAM_BY_SPECIALTY,
      ExportMetricOption.EM_EYE_EXAM_BY_PROVIDER,
      ExportMetricOption.EM_EYE_EXAM_TREND].includes(o.id)).forEach(c => {
      c.loaded = emLoaded;
    });
    const cfpLoaded = !equalCfp;
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.ClinicalFingerprint,
      ExportMetricOption.CptFamily, ExportMetricOption.CptRange, ExportMetricOption.CptCode].includes(o.id)).forEach(c => {
      c.loaded = cfpLoaded;
    });
    const cfpMultilevelLoaded = !equalCfpMultilevel;
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.PROCEDURE_SUMMARY,
      ExportMetricOption.PROCEDURE_SUMMARY_BY_DEPARTMENT, ExportMetricOption.PROCEDURE_SUMMARY_BY_SPECIALTY,
      ExportMetricOption.PROCEDURE_SUMMARY_BY_PROVIDER, ExportMetricOption.PROCEDURE_SUMMARY_BY_DEPARTMENT_BY_FAMILY,
      ExportMetricOption.PROCEDURE_SUMMARY_BY_DEPARTMENT_BY_RANGE, ExportMetricOption.PROCEDURE_SUMMARY_BY_DEPARTMENT_BY_CODE,
      ExportMetricOption.PROCEDURE_SUMMARY_BY_SPECIALTY_BY_FAMILY, ExportMetricOption.PROCEDURE_SUMMARY_BY_SPECIALTY_BY_RANGE,
      ExportMetricOption.PROCEDURE_SUMMARY_BY_SPECIALTY_BY_CODE, ExportMetricOption.PROCEDURE_SUMMARY_BY_PROVIDER_BY_FAMILY,
      ExportMetricOption.PROCEDURE_SUMMARY_BY_PROVIDER_BY_RANGE, ExportMetricOption.PROCEDURE_SUMMARY_BY_PROVIDER_BY_CODE]
      .includes(o.id)).forEach(c => {
        c.loaded = cfpMultilevelLoaded;
    });
    const collectionSummaryLoaded = !equalCollectionSummary;
    const collectionSummaryOption = this.generalMultilevelExportOptions.find(o =>
      o.id === ExportMetricOption.CollectionsSummary);
    if (collectionSummaryOption) {
      collectionSummaryOption.loaded = collectionSummaryLoaded;
    }
    const collectionSnapshotLoaded = !equalCollectionSnapshot;
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.CollectionsByDepartment,
      ExportMetricOption.CollectionsBySpecialty, ExportMetricOption.CollectionsByProvider].includes(o.id)).forEach(c => {
      c.loaded = collectionSnapshotLoaded;
    });
    const collectionPayerLoaded = !equalPayerCollection;
    const collectionPayerOption = this.generalMultilevelExportOptions.find(o => o.id === ExportMetricOption.CollectionsByPayer);
    if (collectionPayerOption) {
      collectionPayerOption.loaded = collectionPayerLoaded;
    }
    const collectionPayerByLevelLoaded = !equalPayerByLevelCollection;
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.CollectionsByPayerByDepartment,
      ExportMetricOption.CollectionsByPayerBySpecialty, ExportMetricOption.CollectionsByPayerByProvider]
      .includes(o.id)).forEach(c => {
      c.loaded = collectionPayerByLevelLoaded;
    });
    const denialsLoaded = !equalDenials;
    this.generalMultilevelExportOptions.filter(o => [ExportMetricOption.DenialsByDepartment,
      ExportMetricOption.DenialsBySpecialty, ExportMetricOption.DenialsByProvider].includes(o.id)).forEach(c => {
      c.loaded = denialsLoaded;
    });
    const denialsPayerLoaded = !equalDenialsPayer;
    const denialsPayerOption = this.generalMultilevelExportOptions.find(o => o.id === ExportMetricOption.DenialsByPayer);
    if (denialsPayerOption) {
      denialsPayerOption.loaded = denialsPayerLoaded;
    }
    this.generalMultilevelExportOptions[0].loaded = wrvuTrendLoaded && wrvuSnapshotLoaded && npvTrendLoaded &&
      npvSnapshotLoaded && (cfpLoaded || !this.canShowClinical) && emLoaded && collectionSummaryLoaded &&
      collectionSnapshotLoaded && collectionPayerLoaded && collectionPayerByLevelLoaded && (this.canHideDenials ||
        (denialsLoaded && denialsPayerLoaded)) && (this.canHideProcedureSummary || cfpMultilevelLoaded);
    this.generalMultilevelExportOptions.filter(x => x.id === ExportMetricOption.WRVU).forEach(y => {
      y.loaded = wrvuTrendLoaded && wrvuSnapshotLoaded;
    });
    this.generalMultilevelExportOptions.filter(x => x.id === ExportMetricOption.NPV).forEach(y => {
      y.loaded = npvTrendLoaded && npvSnapshotLoaded;
    });
    this.generalMultilevelExportOptions.filter(x => x.id === ExportMetricOption.ClinicalFingerprint).forEach(y => {
      y.loaded = cfpLoaded || !this.canShowClinical;
    });
    this.generalMultilevelExportOptions.filter(x => x.id === ExportMetricOption.COLLECTIONS).forEach(y => {
      y.loaded = collectionSummaryLoaded && collectionSnapshotLoaded && collectionPayerLoaded &&
        collectionPayerByLevelLoaded;
    });
    this.generalMultilevelExportOptions.filter(x => x.id === ExportMetricOption.Denials).forEach(y => {
      y.loaded = this.canHideDenials || (denialsLoaded && denialsPayerLoaded);
    });
    this.anyMetricsLoading = !this.generalMultilevelExportOptions[0].loaded;
  }

  private getExportPermitted(): boolean {
    switch (this.chosenExportType) {
      case ExportType.csv:
      case ExportType.excel:
        return !!this.generalMultilevelExportOptions.filter(o => o.selected).length;
      case ExportType.pdf:
        return !!collectPdfOptions(this.pdfOptions).filter(o => o.id !== ExportMetricOption.OVERVIEW && o.selected).length;
    }
  }

  export(): void {
    if (!this.exportPermitted) {
      return;
    }
    switch (this.chosenExportType) {
      case ExportType.excel:
      case ExportType.csv:
        exportToExcelOrCsv(this.generalMultilevelExportOptions, this.stateData, this.display, this.filters,
          this.excelService, this.csv, this.analyticsService, this.chosenExportType, this.npvTelehealthEnabled,
          this.emTelehealthEnabled, this.shouldShowWrvuDepartmentCfteAdjusted,
          this.currentExportFiltersData, this.canShowCfteWrvu, this.canShowCfteCount);
        break;
      case ExportType.pdf:
        const pdfExport = exportToPdf(this.pdfOptions, this.analyticsService);
        this.afterPdfExport.emit([pdfExport[0], pdfExport[1], this.display.communityBenchmarkAlert]);
    }
  }

  setPdfPermitted(): void {
    this.pdfPermitted = this.router.url === overviewPageRoute;
    if (this.router.url === overviewPageRoute) {
      this.setExportTab(ExportType.pdf);
      this.pdfPermitted = true;
    } else {
      this.setExportTab(this.defaultExportType);
      this.pdfPermitted = false;
    }
  }

  getCssClassBasedOnExpansion(depth: number): string {
    switch (depth) {
      case 0:
        return 'metric-display-first';
      case 1:
        return 'metric-display-second';
      case 2:
        return 'metric-display-third';
      case 3:
        return 'metric-display-fourth';
      default:
        return '';
    }
  }

  closeExportOptions(): void {
    this.dialogRef.close();
  }

  private childHasId(options: MultilevelExportOption[], id: ExportMetricOption): boolean {
    for (let x = 0; x < options.length; x++) {
      if (options[x].id === id || this.childHasId(options[x].children, id)) {
        return true;
      }
    }
    return false;
  }

  setDefault(url: string, options: MultilevelExportOption[]): void {
    if (!this.defaultCheckAllowed) {
      return;
    }
    const requiredId: ExportMetricOption = getRequiredId(url, this.cptViewType);
    const foundExpandingOptions = options.filter(o => o.loaded && this.childHasId(o.children, requiredId));
    const foundSelectingOptions = options.filter(o => o.loaded && o.id === requiredId);
    let changed;
    do {
      changed = false;
      foundExpandingOptions.forEach(o => {
        if (!nodeExpansionIsActive(o.nodeExpansionState)) {
          changed = true;
          o.nodeExpansionState = getOppositeNodeExpansionState(o.nodeExpansionState);
        }
        this.setDefault(url, o.children);
      });
    } while (changed);
    do {
      changed = false;
      foundSelectingOptions.forEach(o => {
        if (!o.selected) {
          changed = true;
          o.selected = true;
          this.selectOrDeselectChildren(o);
          if (!nodeExpansionIsActive(o.nodeExpansionState)) {
            o.nodeExpansionState = getOppositeNodeExpansionState(o.nodeExpansionState);
          }
        }
        this.setDefault(url, o.children);
      });
    } while (changed);
    this.resetDisplayedOptions();
    this.exportPermitted = this.getExportPermitted();
  }

  getMultilevelExcelExportMetrics(multilevelExportOptions: MultilevelExportOption[]): void {
    multilevelExportOptions.forEach(option => {
      this.generalMultilevelExportOptions.push(option);
      this.getMultilevelExcelExportMetrics(option.children);
    });
  }

  private setDisplayedExportOptions(options: MultilevelExportOption[], displayed: boolean): void {
    options.forEach(option => {
      option.displayed = displayed && canDisplayExportOption(option.id, this.stateData, this.maxSpecialties,
        this.maxProviders, this.canHideDenials, this.canShowClinical, this.canHideProcedureSummary, this.canHideNpvByLocation);
      this.setDisplayedExportOptions(option.children, option.nodeExpansionState !== NodeExpansionState.COLLAPSED);
    });
  }

  resetDisplayedOptions(): void {
    this.inspectHasLoaded();
    this.setDisplayedExportOptions([this.generalMultilevelExportOptions[0]], true);
    this.generalMultilevelExportOptions[0].loaded = !this.anyMetricsLoading;
    this.displayedMultilevelExportOptions = this.generalMultilevelExportOptions.filter(option => option.displayed);
  }

  whenOptionExpanded(option: MultilevelExportOption, event?: MouseEvent): void {
    if (event) {
      this.defaultCheckAllowed = false;
    }
    if (!(option.id === ExportMetricOption.ALL)) {
      const originalState = option.nodeExpansionState;
      this.generalMultilevelExportOptions.forEach(o => {
        if (o.nodeExpansionState === NodeExpansionState.EXPANDED && (o.depth || 0) > (option.depth || 0)) {
          o.nodeExpansionState = NodeExpansionState.COLLAPSED;
        }
      });
      option.nodeExpansionState = getOppositeNodeExpansionState(originalState);
      this.resetDisplayedOptions();
    }
  }

  whenOptionSelected(option: MultilevelExportOption, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (event) {
      this.defaultCheckAllowed = false;
      if (!option.loaded) {
        return;
      }
    }
    option.selected = !option.selected;
    this.selectOrDeselectChildren(option);
    selectOrDeselectParents(this.generalMultilevelExportOptions);
    this.exportPermitted = this.getExportPermitted();
  }

  private selectOrDeselectChildren(option: MultilevelExportOption): void {
    option.children.filter(c => canDisplayExportOption(c.id, this.stateData, this.maxSpecialties,
      this.maxProviders, this.canHideDenials, this.canShowClinical, this.canHideProcedureSummary,
      this.canHideNpvByLocation)).forEach(child => {
      child.selected = option.selected;
      this.selectOrDeselectChildren(child);
    });
  }

  setExportTab(exportType: ExportType): void {
    this.chosenExportType = exportType;
    this.exportPermitted = this.getExportPermitted();
  }

  afterPdfSelected = (options: MultilevelExportOption[]) => {
    this.pdfOptions = options;
    this.exportPermitted = this.getExportPermitted();
  };
}
